/** Versioned public OpenFEC progress, normalized once at the API boundary. */
export interface OpenFecProgress {
  kind: 'openfec';
  schemaVersion: 1;
  phase: string | null;
  endpointCategory: string | null;
  currentPage: number | null;
  streamsCompleted: number | null;
  streamsDiscovered: number | null;
  pagesSaved: number | null;
  rowsReceived: number | null;
  startedAt: string | null;
  heartbeatAt: string | null;
  lastCommittedPageAt: string | null;
  elapsedMs: number | null;
  requestsThisAttempt: number | null;
  maxRequests: number | null;
  retriesThisAttempt: number | null;
  requestIntervalMs: number | null;
  effectiveRequestIntervalMs: number | null;
  observedRequestIntervalMs: number | null;
  cadenceSampleCount: number | null;
  cadenceBasis: string | null;
  waitingReason: string | null;
  waitUntil: string | null;
  staleAfterMs: number | null;
  runId: string | null;
  jobId: string | null;
  sourceId: string | null;
  outcome: string | null;
}

export function normalizeOpenFecProgress(value: unknown): OpenFecProgress | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (raw.kind !== 'openfec' || raw.schema_version !== 1) return null;
  const text = (key: string): string | null => typeof raw[key] === 'string' ? raw[key] as string : null;
  const count = (key: string): number | null => typeof raw[key] === 'number' && Number.isFinite(raw[key]) && (raw[key] as number) >= 0 ? raw[key] as number : null;
  const id = (key: string): string | null => {
    const value = raw[key];
    return (typeof value === 'string' || typeof value === 'number') && /^[a-zA-Z0-9_-]{1,128}$/.test(String(value)) ? String(value) : null;
  };
  return {
    kind: 'openfec', schemaVersion: 1,
    phase: text('phase'), endpointCategory: text('endpoint_category'), currentPage: count('current_page'),
    streamsCompleted: count('streams_completed'), streamsDiscovered: count('streams_discovered'),
    pagesSaved: count('pages_saved'), rowsReceived: count('rows_received'),
    startedAt: text('started_at'), heartbeatAt: text('heartbeat_at'), lastCommittedPageAt: text('last_committed_page_at'), elapsedMs: count('elapsed_ms'),
    requestsThisAttempt: count('requests_this_attempt'), maxRequests: count('max_requests'), retriesThisAttempt: count('retries_this_attempt'),
    requestIntervalMs: count('request_interval_ms'), effectiveRequestIntervalMs: count('effective_request_interval_ms'), observedRequestIntervalMs: count('observed_request_interval_ms'),
    cadenceSampleCount: count('cadence_sample_count'), cadenceBasis: text('cadence_basis'), waitingReason: text('waiting_reason'), waitUntil: text('wait_until'), staleAfterMs: count('stale_after_ms'),
    runId: id('run_id'), jobId: id('job_id'), sourceId: id('source_id'), outcome: text('outcome'),
  };
}

/** The backend allowance already includes request timeouts and known waits. */
export function isOpenFecHeartbeatStale(progress: OpenFecProgress, active: boolean, now = Date.now()): boolean {
  if (!active || progress.outcome || !progress.heartbeatAt || progress.staleAfterMs === null) return false;
  const heartbeat = Date.parse(progress.heartbeatAt);
  return Number.isFinite(heartbeat) && now > heartbeat + progress.staleAfterMs;
}

/** Legacy telemetry may be absent or malformed; never coerce unknown into zero. */
export function normalizeJobPercentage(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}
